.linkComponent {
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.button {
  position: relative;
  box-shadow: none;
  outline-offset: 0;
  border: 2px solid transparent;
  user-select: none;
  touch-action: manipulation;
  white-space: nowrap;
  padding: 0;
  border-radius: var(--btn-radius);

  .content {
    text-shadow: none;
    font-weight: bold;
    display: flex;
    align-items: center;
    justify-content: center;
    line-height: 1;
  }

  &:not(.rounded) {
    --btn-radius: 4px;
  }

  &.rounded {
    --btn-radius: var(--btn-rounded-radius);
  }

  &:not([disabled]),
  &:not([aria-disabled="true"]) {
    cursor: pointer;
  }

  &[disabled],
  &[aria-disabled="true"] {
    text-shadow: none;
    box-shadow: none;
    cursor: default;
  }

  &:focus::after {
    content: "";
    position: absolute;
    top: -3px;
    left: -3px;
    bottom: -3px;
    right: -3px;
    border-radius: var(--btn-radius);
    box-shadow: 0 0 0 2px rgba(0, 147, 207, 0.62);
    z-index: 0;
  }
}

.shrink {
  min-width: auto;
}

.block {
  width: 100%;
}

.callToAction {
  min-width: 200px;
}

.loading {
  display: flex;
  justify-content: center;
  align-items: center;
  position: absolute;
  width: 100%;
  height: 100%;
  border-radius: var(--btn-radius);
}

// type
// tertiary is also the default type
.tertiary {
  color: var(--color-tertiary-900);
  background: white;
  border: 2px solid var(--color-tertiary-900);
  padding: 0 16px;

  &[aria-disabled="true"] {
    border: 2px solid var(--color-tertiary-400);
    color: var(--color-tertiary-400);
  }

  &:active,
  &:focus {
    background-color: white;
    outline: 0;
  }

  &:hover:not([aria-disabled="true"]) {
    background: var(--color-gray-100);
    color: var(--color-tertiary-900);
  }

  .loading {
    background: white;
    color: var(--color-tertiary-400);
  }
}

.primary {
  color: var(--color-tertiary-900);
  background: var(--color-primary-400);
  border-color: var(--color-primary-400);
  padding: 0 16px;

  &[aria-disabled="true"] {
    background: var(--color-primary-200);
    border-color: var(--color-primary-200);

    .content {
      opacity: 0.5;
    }
  }

  &:hover {
    color: var(--color-tertiary-900);
  }

  &:active,
  &:focus {
    outline: 0;
  }

  &:active:not([aria-disabled="true"]),
  &:focus:not([aria-disabled="true"]) {
    border-color: var(--color-primary-400);
    background-color: var(--color-primary-400);
    outline: 0;
  }

  &:hover:not([aria-disabled="true"]) {
    background-color: var(--color-primary-600);
    border-color: var(--color-primary-600);
  }

  .loading {
    background: var(--color-primary-200);
  }
}

.secondary {
  color: white;
  background: var(--color-tertiary-800);
  border-color: var(--color-tertiary-800);
  padding: 0 16px;

  &[aria-disabled="true"] {
    background: var(--color-tertiary-400);
    border-color: var(--color-tertiary-400);
  }

  &:hover {
    color: white;
  }

  &:active,
  &:focus {
    outline: 0;
  }

  &:active:not([aria-disabled="true"]),
  &:focus:not([aria-disabled="true"]) {
    background-color: var(--color-tertiary-800);
    border-color: var(--color-tertiary-800);
  }

  &:hover:not([aria-disabled="true"]) {
    background: var(--color-tertiary-900);
    border-color: var(--color-tertiary-900);
  }

  .loading {
    background: var(--color-tertiary-400);
  }
}

.danger {
  color: white;
  background: var(--color-red-700);
  border-color: var(--color-red-700);
  padding: 0 16px;

  &[aria-disabled="true"] {
    background: var(--color-red-350);
    border-color: var(--color-red-350);
  }

  &:hover {
    color: white;
  }

  &:active,
  &:focus {
    outline: 0;
  }

  &:active:not([aria-disabled="true"]),
  &:focus:not([aria-disabled="true"]) {
    background-color: var(--color-red-700);
    border-color: var(--color-red-700);
  }

  &:hover:not([aria-disabled="true"]) {
    background-color: var(--color-red-900);
    border-color: var(--color-red-900);
  }

  .loading {
    background: var(--color-red-350);
  }
}

.dangerTertiary {
  color: var(--color-red-700);
  background: white;
  border: 2px solid var(--color-red-700);
  padding: 0 16px;

  &[aria-disabled="true"] {
    border: 2px solid var(--color-red-350);
    color: var(--color-red-350);
  }

  &:active,
  &:focus {
    background-color: white;
    outline: 0;
  }

  &:hover:not([aria-disabled="true"]) {
    background: var(--color-gray-100);
    color: var(--color-red-900);
    border: 2px solid var(--color-red-900);
  }

  .loading {
    background: white;
    color: var(--color-red-350);
  }
}

.text {
  color: var(--color-tertiary-900);
  background: transparent;
  border-color: transparent;
  padding: 0 16px;

  &[aria-disabled="true"] {
    color: var(--color-tertiary-400);
  }

  &:focus {
    outline: 0;
  }

  &:active:not([aria-disabled="true"]) {
    text-decoration: underline;
    color: var(--color-secondary-900);
    outline: 0;
  }

  &:hover:not([aria-disabled="true"]) {
    text-decoration: underline;
    color: var(--color-secondary-900);
  }

  .loading {
    outline: 0;
  }
}

.textOnlyLink {
  .text;

  display: inline-block;
  height: auto;
  padding: 0;
  margin: 0;
  border: none;
}

.textOnlyLinkLight {
  .textOnlyLink;

  text-decoration: underline;
  color: var(--color-tertiary-800);
  /* stylelint-disable-next-line no-descending-specificity */
  .content {
    font-weight: normal;
  }
}

.textOnly {
  color: var(--color-tertiary-900);
  background: transparent;
  border: 0;

  &[aria-disabled="true"] {
    color: var(--color-tertiary-400);
  }

  &:focus {
    outline: 0;
  }

  &:active:not([aria-disabled="true"]) {
    text-decoration: underline;
    color: var(--color-secondary-900);
    outline: 0;
  }

  &:hover:not([aria-disabled="true"]) {
    text-decoration: underline;
    color: var(--color-secondary-900);
  }

  .loading {
    outline: 0;
  }
}

.textOnlyLight {
  .textOnly;

  color: var(--color-tertiary-800);
  /* stylelint-disable-next-line no-descending-specificity */
  .content {
    font-weight: normal;
  }
}

.dangerTextOnly {
  color: var(--color-red-700);
  background: transparent;
  border: 0;

  &[aria-disabled="true"] {
    color: var(--color-red-350);
  }

  &:focus {
    outline: 0;
  }

  &:active:not([aria-disabled="true"]) {
    text-decoration: underline;
    color: var(--color-red-900);
    outline: 0;
  }

  &:hover:not([aria-disabled="true"]) {
    text-decoration: underline;
    color: var(--color-red-900);
  }

  .loading {
    outline: 0;
  }
}

// size
.xtraSmall {
  --btn-rounded-radius: 24px;

  font-size: 0.875rem;

  &:not(.shrink):not(.block):not(.callToAction) {
    min-width: 104px;
  }
  &:not(.textOnly):not(.dangerTextOnly):not(.textOnlyLink):not(.textOnlyLinkLight) {
    height: 32px;
  }
}
.small {
  --btn-rounded-radius: 24px;

  font-size: 0.875rem;

  &:not(.shrink):not(.block):not(.callToAction) {
    min-width: 104px;
  }
  &:not(.textOnly):not(.dangerTextOnly):not(.textOnlyLight):not(.textOnlyLink):not(
      .textOnlyLinkLight
    ) {
    height: 40px;
  }
}
.middle {
  --btn-rounded-radius: 32px;

  font-size: 1rem;

  &:not(.shrink):not(.block):not(.callToAction) {
    min-width: 200px;
  }
  &:not(.textOnly):not(.dangerTextOnly):not(.textOnlyLink):not(.textOnlyLinkLight) {
    height: 48px;
  }
}
.large {
  --btn-rounded-radius: 48px;

  font-size: 1.125rem;

  &:not(.shrink):not(.block):not(.callToAction) {
    min-width: 200px;
  }
  &:not(.textOnly):not(.dangerTextOnly):not(.textOnlyLink):not(.textOnlyLinkLight) {
    height: 56px;
  }
}

.icon {
  transition: margin-left 0.3s cubic-bezier(0.645, 0.045, 0.355, 1);
  line-height: 1;
}
